import type { MaybeRefOrGetter } from "vue";
import { computed, onScopeDispose, ref, toValue, watch } from "vue";

const overlayDepth = ref(0);

/** True while any shortcut overlay (e.g. the command palette) is open. */
export const isShortcutOverlayOpen = computed(() => overlayDepth.value > 0);

/**
 * Marks the owning component as a shortcut overlay. While active, shortcuts not
 * explicitly marked `overlayOwned` are rejected before consuming the event, so
 * the overlay keeps ownership of its keys and background UI cannot change.
 */
export function useShortcutOverlay(active: MaybeRefOrGetter<boolean>): void {
  const owns = ref(false);

  watch(
    () => toValue(active),
    (value) => {
      if (value && !owns.value) {
        owns.value = true;
        overlayDepth.value += 1;
      } else if (!value && owns.value) {
        owns.value = false;
        overlayDepth.value = Math.max(0, overlayDepth.value - 1);
      }
    },
    { immediate: true }
  );

  onScopeDispose(() => {
    if (!owns.value) return;
    owns.value = false;
    overlayDepth.value = Math.max(0, overlayDepth.value - 1);
  });
}
